import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import { fmtUsd, fmtNum } from '../lib/format.js';
import { locationKeyFromName } from '../lib/schedule.js';
import { ReportsSkeleton } from './ReportsSkeleton.jsx';
import { ScorecardCard } from './ScorecardCard.jsx';
import { ConfidencePill } from './ConfidencePill.jsx';
import { DailyForecastBars } from './DailyForecastBars.jsx';
import { EventCard } from './EventCard.jsx';
import { WeatherCard } from './WeatherCard.jsx';

export function Step3View({ location, user }) {
  const isManager = user && window.EmpireRoles ? window.EmpireRoles.isManager(user) : false;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // CJ 2026-05-16: rolling per-day actuals overlaid on the forecast bars.
  // Keyed by YYYY-MM-DD. Frontend fan-out into /api/realtime/:key?date= so
  // we don't have to widen the step3 endpoint; the realtime cache covers
  // repeat hits.
  const [dailyActuals, setDailyActuals] = useState({});
  const locationKey = locationKeyFromName(location?.name);

  const load = useCallback(async ({ force = false } = {}) => {
    if (!locationKey) return;
    try {
      const url = `/api/weekly/step3/${locationKey}${force ? '?force=1' : ''}`;
      const res = await api('GET', url);
      setData(res); setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load this-week plan');
    } finally {
      setLoading(false);
    }
  }, [locationKey]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // Pull per-day actuals for the current week (Mon → today) once the
  // step3 payload has framed the week range. Past days come back from
  // the realtime endpoint's longer cache; today's value is partial.
  useEffect(() => {
    if (!locationKey || !data?.meta?.isoWeekInfo?.startDate) return;
    const todayYmd = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const start = data.meta.isoWeekInfo.startDate;
    const days = [];
    const d = new Date(start + 'T12:00:00Z');
    for (let i = 0; i < 7; i++) {
      const ymd = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
      }).format(d);
      if (ymd <= todayYmd) days.push(ymd);
      d.setUTCDate(d.getUTCDate() + 1);
    }
    let cancelled = false;
    Promise.all(days.map(ymd =>
      api('GET', `/api/realtime/${locationKey}?date=${ymd}`)
        .then(r => [ymd, { tx: Number(r?.scorecard?.transactions?.value) || 0, isToday: ymd === todayYmd }])
        .catch(() => [ymd, null])
    )).then(rows => {
      if (cancelled) return;
      const out = {};
      for (const [ymd, v] of rows) if (v) out[ymd] = v;
      setDailyActuals(out);
    });
    return () => { cancelled = true; };
  }, [locationKey, data?.meta?.isoWeekInfo?.startDate]);

  if (!locationKey) return <div className="card text-sm text-gray-500">Pick a location.</div>;
  if (loading && !data) return <ReportsSkeleton label="Loading this week's plan…" />;
  if (error) return <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
    <div className="text-sm font-semibold text-red-700 mb-1">This week unavailable</div>
    <div className="text-xs text-gray-600">{error}</div>
    <button className="btn-secondary mt-3" onClick={load}>Retry</button>
  </div>;
  if (!data) return null;

  const { meta, step3 } = data;
  const f = step3.thisWeekForecast || {};
  const hasForecast = (f.projected_transactions > 0 || f.projected_revenue > 0 || f.confidence || f.event_adjustment);
  const events = step3.events || [];
  const weather = step3.weather || { available: false, days: [] };
  const dailyForecast = step3.dailyForecast || [];

  return (
    <div className="space-y-3">
      <div style={{ fontSize: 12, color: '#757575' }}>
        {meta.locationName} · {meta.isoWeekInfo.weekStr} · {meta.isoWeekInfo.startDate} → {meta.isoWeekInfo.endDate}
      </div>

      {!meta.pis.available && (
        <div className="card" style={{ borderLeft: '4px solid #f9a825', background: '#fff8e1' }}>
          <div className="text-sm font-semibold" style={{ color: '#a16207' }}>Forecast & events not yet wired</div>
          <div className="text-xs" style={{ color: '#5d4037', marginTop: 4 }}>
            Weather is live below. Set <code>PIS_API_KEY</code> in <code>.env</code> to enable
            projected transactions, revenue, and the events feed from <code>pis.empireteaandcoffee.com</code>.
            ({meta.pis.reason === 'unconfigured' ? 'unconfigured' : `error: ${meta.pis.error || meta.pis.reason}`})
          </div>
        </div>
      )}

      {hasForecast && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          <ScorecardCard label="Projected Tx" value={fmtNum(f.projected_transactions)}
            sub={null} flag="green" />
          {isManager && (
            <ScorecardCard label="Projected Revenue" value={fmtUsd(f.projected_revenue)}
              sub={null} flag="green" />
          )}
          {f.confidence && (
            <div className="card" style={{ padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#757575', textTransform: 'uppercase' }}>Confidence</div>
              <div style={{ marginTop: 8 }}><ConfidencePill confidence={f.confidence} /></div>
            </div>
          )}
          {f.event_adjustment && (
            <div className="card" style={{ padding: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#757575', textTransform: 'uppercase' }}>Event Adjustment</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#212121', marginTop: 4 }}>{f.event_adjustment}</div>
            </div>
          )}
        </div>
      )}

      <DailyForecastBars rows={dailyForecast} actuals={dailyActuals} weekStart={meta.isoWeekInfo.startDate} weekEnd={meta.isoWeekInfo.endDate} />

      {events.length > 0 && (
        <div className="card">
          <div style={{ fontSize: 13, fontWeight: 700, color: '#212121', marginBottom: 10 }}>Events This Week</div>
          {events.map((e, i) => <EventCard key={i} event={e} />)}
        </div>
      )}

      <div className="card">
        <div style={{ fontSize: 13, fontWeight: 700, color: '#212121', marginBottom: 10 }}>Weather Forecast</div>
        {weather.available && weather.days.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${weather.days.length}, minmax(0, 1fr))`, gap: 6 }}>
            {weather.days.map((d) => <WeatherCard key={d.date} day={d} />)}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: '#757575' }}>{weather.message || 'No weather data.'}</div>
        )}
      </div>
    </div>
  );
}
